import axios from 'axios';
import SessionService from './SessionService';
import type { LancamentoSituacao } from '../screens/financeiro/financeiroShared';

/**
 * Base da API (ex.: https://api.ligchat.com/api). Sem barra final.
 * Os demais serviços usam `${REACT_APP_API_URL}/users/...`, não `/api/users/...`.
 */
function baseUrl(): string {
  const u = process.env.REACT_APP_API_URL;
  if (!u) {
    throw new Error('REACT_APP_API_URL não configurada');
  }
  return u.replace(/\/$/, '');
}

/** Rota relativa ao host ASP.NET: api/financeiro → path /financeiro/... quando a base já termina em /api. */
function financeiroUrl(suffix: string): string {
  const base = baseUrl();
  const s = suffix.startsWith('/') ? suffix : `/${suffix}`;
  if (base.endsWith('/api')) {
    return `${base}${s}`;
  }
  return `${base}/api${s}`;
}

export type CadastroItemDto = { id: number; name: string };

export type PagamentoView = {
  id?: number;
  valor: number;
  data: string;
  responsavel?: string;
  metodo?: string;
};

export type LancamentoView = {
  id: string;
  tipo: 'entrada' | 'saida';
  data: string;
  descricao: string;
  cliente?: string;
  responsavel?: string;
  categoria?: string;
  planoDeContas?: string;
  metodo?: string;
  valor: number;
  valorPago: number;
  vencimento?: string;
  dataPagamento?: string;
  parcelaAtual?: number;
  totalParcelas?: number;
  situacao: LancamentoSituacao;
  anexo?: string;
  anexoUrl?: string;
  pagamentos: PagamentoView[];
};

export const FinanceiroCadastroKind = {
  Cliente: 0,
  ResponsavelPagamento: 1,
  Categoria: 2,
  MetodoPagamento: 3,
  PlanoDeContas: 4,
  CategoriaJuridicaEntrada: 5,
  CategoriaJuridicaSaida: 6,
  PlanoDeContasEntrada: 7,
  PlanoDeContasSaida: 8,
} as const;

export type FinanceiroCadastroKindNum = (typeof FinanceiroCadastroKind)[keyof typeof FinanceiroCadastroKind];

function authHeaders(): { Authorization: string } {
  const token = SessionService.getToken();
  if (!token) {
    throw new Error('Não autenticado');
  }
  return { Authorization: `Bearer ${token}` };
}

export interface FinanceiroCadastrosResponse {
  clientes: CadastroItemDto[];
  responsaveis: CadastroItemDto[];
  metodos: CadastroItemDto[];
  categorias?: CadastroItemDto[];
  planosDeContasEntrada?: CadastroItemDto[];
  planosDeContasSaida?: CadastroItemDto[];
  planosDeContas?: CadastroItemDto[];
}

export async function getCadastros(): Promise<FinanceiroCadastrosResponse> {
  const { data } = await axios.get<FinanceiroCadastrosResponse>(
    `${financeiroUrl('/financeiro/cadastros')}`,
    { headers: authHeaders() }
  );
  const planosEntrada = data.planosDeContasEntrada ?? data.planosDeContas ?? [];
  return {
    ...data,
    categorias: data.categorias ?? [],
    planosDeContasEntrada: planosEntrada,
    planosDeContasSaida: data.planosDeContasSaida ?? [],
  };
}

export async function addCadastro(kind: FinanceiroCadastroKindNum, name: string): Promise<CadastroItemDto> {
  const { data } = await axios.post<CadastroItemDto>(
    `${financeiroUrl('/financeiro/cadastros')}`,
    { kind, name },
    { headers: authHeaders() }
  );
  return data;
}

export async function removeCadastro(id: number): Promise<void> {
  await axios.delete(`${financeiroUrl(`/financeiro/cadastros/${id}`)}`, { headers: authHeaders() });
}

export async function updateCadastro(id: number, name: string): Promise<CadastroItemDto> {
  const { data } = await axios.put<CadastroItemDto>(
    `${financeiroUrl(`/financeiro/cadastros/${id}`)}`,
    { name: name.trim() },
    { headers: authHeaders() }
  );
  return data;
}

interface PagamentoApiDto {
  id: number;
  valor: number;
  data: string;
  responsavel?: string | null;
  metodo?: string | null;
}

interface LancamentoApiDto {
  id: number;
  tipo: string;
  data: string;
  vencimento?: string | null;
  dataPagamento?: string | null;
  descricao: string;
  cliente?: string | null;
  responsavel?: string | null;
  categoria?: string | null;
  planoDeContas?: string | null;
  metodo?: string | null;
  valor: number;
  valorPago: number;
  parcelaAtual?: number | null;
  totalParcelas?: number | null;
  situacao?: string | null;
  anexoNome?: string | null;
  anexoUrl?: string | null;
  pagamentos?: PagamentoApiDto[] | null;
}

function pickAnexoNome(d: LancamentoApiDto): string | undefined {
  const n = d.anexoNome ?? (d as { AnexoNome?: string | null }).AnexoNome;
  return n ?? undefined;
}

function pickAnexoUrl(d: LancamentoApiDto): string | undefined {
  const u = d.anexoUrl ?? (d as { AnexoUrl?: string | null }).AnexoUrl;
  return u ?? undefined;
}

function normalizeSituacao(raw: string | undefined | null): LancamentoSituacao {
  const s = (raw ?? '').toLowerCase();
  if (s === 'pago') return 'pago';
  if (s === 'atrasado') return 'atrasado';
  return 'pendente';
}

export function mapLancamentoDtoToView(d: LancamentoApiDto): LancamentoView {
  const tipo = d.tipo === 'saida' ? 'saida' : 'entrada';
  const situacaoRaw =
    (d as { situacao?: string | null }).situacao ?? (d as { Situacao?: string | null }).Situacao;
  const planoRaw =
    d.planoDeContas ?? (d as { PlanoDeContas?: string | null }).PlanoDeContas ?? undefined;
  const pagamentosRaw =
    d.pagamentos ?? (d as { Pagamentos?: PagamentoApiDto[] | null }).Pagamentos ?? [];
  return {
    id: String(d.id),
    tipo,
    data: d.data,
    descricao: d.descricao,
    cliente: d.cliente ?? undefined,
    responsavel: d.responsavel ?? undefined,
    categoria: d.categoria ?? undefined,
    planoDeContas: planoRaw ?? undefined,
    metodo: d.metodo ?? undefined,
    valor: Number(d.valor),
    valorPago: Number(d.valorPago),
    vencimento: d.vencimento ?? undefined,
    dataPagamento:
      d.dataPagamento ?? (d as { DataPagamento?: string | null }).DataPagamento ?? undefined,
    parcelaAtual: d.parcelaAtual ?? undefined,
    totalParcelas: d.totalParcelas ?? undefined,
    situacao: normalizeSituacao(situacaoRaw),
    anexo: pickAnexoNome(d),
    anexoUrl: pickAnexoUrl(d),
    pagamentos: (pagamentosRaw ?? []).map((p) => ({
      id: p.id,
      valor: Number(p.valor),
      data: p.data,
      responsavel: p.responsavel ?? undefined,
      metodo: p.metodo ?? (p as { Metodo?: string | null }).Metodo ?? undefined,
    })),
  };
}

export async function getLancamentos(): Promise<LancamentoView[]> {
  const { data } = await axios.get<LancamentoApiDto[]>(`${financeiroUrl('/financeiro/lancamentos')}`, {
    headers: authHeaders(),
  });
  return data.map(mapLancamentoDtoToView);
}

export interface CreateLancamentoPayload {
  tipo: 'entrada' | 'saida';
  data: string;
  vencimento?: string;
  dataPagamento?: string;
  descricao: string;
  cliente?: string;
  responsavel?: string;
  categoria?: string;
  planoDeContas?: string;
  metodo?: string;
  valor: number;
  valorPago: number;
  parcelaAtual?: number;
  totalParcelas?: number;
  anexoNome?: string;
  anexoBase64?: string;
  anexoContentType?: string;
  /** Enviar "pago" para quitar (API define ValorPago = Valor). */
  situacao?: 'pago';
  /**
   * Quando informado, substitui o histórico de pagamentos no servidor.
   * ValorPago é recalculado como sum(pagamentos.valor); o campo `valorPago` acima é ignorado.
   */
  pagamentos?: Array<{ valor: number; data: string; responsavel?: string; metodo?: string }>;
}

function payloadToApiBody(payload: CreateLancamentoPayload) {
  return {
    tipo: payload.tipo,
    data: payload.data,
    vencimento: payload.vencimento || null,
    dataPagamento: payload.dataPagamento || null,
    descricao: payload.descricao,
    cliente: payload.cliente || null,
    responsavel: payload.responsavel || null,
    categoria: payload.categoria || null,
    planoDeContas: payload.planoDeContas || null,
    metodo: payload.metodo || null,
    valor: payload.valor,
    valorPago: payload.valorPago,
    parcelaAtual: payload.parcelaAtual ?? null,
    totalParcelas: payload.totalParcelas ?? null,
    anexoNome: payload.anexoNome || null,
    anexoBase64: payload.anexoBase64 ?? null,
    anexoContentType: payload.anexoContentType ?? null,
    ...(payload.situacao ? { situacao: payload.situacao } : {}),
    ...(payload.pagamentos
      ? {
          pagamentos: payload.pagamentos.map((p) => ({
            valor: p.valor,
            data: p.data,
            responsavel: p.responsavel || null,
            metodo: p.metodo || null,
          })),
        }
      : {}),
  };
}

export async function createLancamento(payload: CreateLancamentoPayload): Promise<LancamentoView> {
  const body = payloadToApiBody(payload);
  const { data } = await axios.post<LancamentoApiDto>(`${financeiroUrl('/financeiro/lancamentos')}`, body, {
    headers: authHeaders(),
  });
  return mapLancamentoDtoToView(data);
}

export async function createLancamentosBatch(payloads: CreateLancamentoPayload[]): Promise<LancamentoView[]> {
  const items = payloads.map(payloadToApiBody);
  const { data } = await axios.post<LancamentoApiDto[]>(
    `${financeiroUrl('/financeiro/lancamentos/batch')}`,
    { items },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return data.map(mapLancamentoDtoToView);
}

export async function updateLancamento(id: string, payload: CreateLancamentoPayload): Promise<LancamentoView> {
  const body = payloadToApiBody(payload);
  const { data } = await axios.put<LancamentoApiDto>(
    `${financeiroUrl(`/financeiro/lancamentos/${encodeURIComponent(id)}`)}`,
    body,
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return mapLancamentoDtoToView(data);
}

export async function deleteLancamento(id: string): Promise<void> {
  await axios.delete(`${financeiroUrl(`/financeiro/lancamentos/${encodeURIComponent(id)}`)}`, {
    headers: authHeaders(),
  });
}
